// Copyright 2018-2024 the Deno authors. All rights reserved. MIT license.
// This module is browser compatible.
import type { Range, SemVer } from "./types.ts";
import { satisfies } from "./satisfies.ts";
import { greaterThan } from "./greater_than.ts";

/**
 * Returns the highest SemVer in the list that satisfies the range, or `undefined`
 * if none of them do.
 *
 * @example Usage
 * ```ts
 * import { parse, parseRange, maxSatisfying } from "@std/semver";
 * import { assertEquals } from "@std/assert";
 *
 * const versions = ["1.2.3", "1.2.4", "1.3.0", "2.0.0", "2.1.0"].map(parse);
 * const range = parseRange(">=1.0.0 <2.0.0");
 *
 * assertEquals(maxSatisfying(versions, range), parse("1.3.0"));
 * ```
 *
 * @param versions The versions to check.
 * @param range The range of possible versions to compare to.
 * @returns The highest version in versions that satisfies the range.
 */
export function maxSatisfying(
  versions: SemVer[],
  range: Range,
): SemVer | undefined {
  let max;
  for (const version of versions) {
    if (!satisfies(version, range)) continue;
    max = max && greaterThan(max, version) ? max : version;
  }
  return max;
}

// denoCacheMetadata={"headers":{"x-goog-storage-class":"STANDARD","via":"1.1 google","date":"Fri, 08 Nov 2024 09:36:15 GMT","server":"UploadServer","content-security-policy":"default-src 'none'; script-src 'none'; style-src 'none'; img-src 'none'; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; frame-ancestors 'none'; sandbox; form-action 'none';","x-goog-stored-content-encoding":"identity","x-guploader-uploadid":"AHmUCY3j-9kefE-X8h1Efzz3d5eS312xdpEiQkzelOdVmWAlL_Fpf_m4Dqh4CvJdkMVw2ggY6qJwLvPlqw","x-jsr-cache-status":"hit","x-content-type-options":"nosniff","expires":"Sat, 08 Nov 2025 09:36:15 GMT","age":"2026","x-jsr-cache-id":"IAD","x-goog-stored-content-length":"1160","alt-svc":"h3=\":443\"; ma=2592000,h3-29=\":443\"; ma=2592000","x-goog-hash":"crc32c=1GRkhw==,md5=+AxtVhuBHMZEqWOPFLwqIA==","access-control-expose-headers":"*","etag":"\"f80c6d561b811cc644a9638f14bc2a20\"","accept-ranges":"bytes","x-goog-generation":"1725446501955066","content-length":"1160","x-robots-tag":"noindex","access-control-allow-origin":"*","last-modified":"Wed, 04 Sep 2024 10:41:41 GMT","cross-origin-resource-policy":"cross-origin","content-type":"text/typescript","x-goog-metageneration":"1","cache-control":"public, max-age=31536000, immutable"},"url":"https://jsr.io/@std/semver/1.0.3/max_satisfying.ts","time":1731060601}